/**
 * Enum for representing the supported languages.
 * The enum keys represent the language name and the values represent the language code.
 * @enum {string}
 */
export enum Language {
  Afrikaans = 'af',
  Albanian = 'sq',
  Amharic = 'am',
  Arabic = 'ar',
  Armenian = 'hy',
  Azerbaijani = 'az',
  Bengali = 'bn',
  Bosnian = 'bs',
  Bulgarian = 'bg',
  Catalan = 'ca',
  Chinese_Simplified = 'zh',
  Chinese_Traditional = 'zh-TW',
  Croatian = 'hr',
  Czech = 'cs',
  Danish = 'da',
  Dari = 'fa-AF',
  Dutch = 'nl',
  English = 'en',
  Estonian = 'et',
  Farsi_Persian = 'fa',
  Filipino_Tagalog = 'tl',
  Finnish = 'fi',
  French = 'fr',
  French_Canada = 'fr-CA',
  Georgian = 'ka',
  German = 'de',
  Greek = 'el',
  Gujarati = 'gu',
  Haitian_Creole = 'ht',
  Hausa = 'ha',
  Hebrew = 'he',
  Hindi = 'hi',
  Hungarian = 'hu',
  Icelandic = 'is',
  Indonesian = 'id',
  Irish = 'ga',
  Italian = 'it',
  Japanese = 'ja',
  Kannada = 'kn',
  Kazakh = 'kk',
  Korean = 'ko',
  Latvian = 'lv',
  Lithuanian = 'lt',
  Macedonian = 'mk',
  Malay = 'ms',
  Malayalam = 'ml',
  Maltese = 'mt',
  Marathi = 'mr',
  Mongolian = 'mn',
  Norwegian_Bokmal = 'no',
  Pashto = 'ps',
  Polish = 'pl',
  Portuguese_Brazil = 'pt',
  Portuguese_Portugal = 'pt-PT',
  Punjabi = 'pa',
  Romanian = 'ro',
  Russian = 'ru',
  Serbian = 'sr',
  Sinhala = 'si',
  Slovak = 'sk',
  Slovenian = 'sl',
  Somali = 'so',
  Spanish = 'es',
  Spanish_Mexico = 'es-MX',
  Swahili = 'sw',
  Swedish = 'sv',
  Tamil = 'ta',
  Telugu = 'te',
  Thai = 'th',
  Turkish = 'tr',
  Ukrainian = 'uk',
  Urdu = 'ur',
  Uzbek = 'uz',
  Vietnamese = 'vi',
  Welsh = 'cy'
}

/**
 * This TypeScript module exports a dictionary object that maps language identifiers to their native language names.
 * The dictionary is designed to support a wide range of languages, providing an easy reference for applications
 * that need to display language names in their native scripts. Each key in the dictionary is a member of the `Language`
 * enum, ensuring that only valid language identifiers are used. The values are strings representing the name of the
 * language in its native script or form. This setup facilitates internationalization and localization efforts by
 * providing a centralized resource for language names.
 *
 * Usage example:
 * Suppose you have a dropdown menu for language selection in your application. You can use `languageDictionary`
 * to populate the dropdown options with the native names of the languages. This enhances the user experience by
 * allowing users to find and select their language of choice more intuitively.
 */
export const languageDictionary: { [key in Language]: string } = {
  [Language.Afrikaans]: 'Afrikaans',
  [Language.Albanian]: 'Albanian',
  [Language.Amharic]: 'Amharic',
  [Language.Arabic]: 'Arabic',
  [Language.Armenian]: 'Armenian',
  [Language.Azerbaijani]: 'Azerbaijani',
  [Language.Bengali]: 'Bengali',
  [Language.Bosnian]: 'Bosnian',
  [Language.Bulgarian]: 'Bulgarian',
  [Language.Catalan]: 'Catalan',
  [Language.Chinese_Simplified]: 'Chinese (Simplified)',
  [Language.Chinese_Traditional]: 'Chinese (Traditional)',
  [Language.Croatian]: 'Croatian',
  [Language.Czech]: 'Czech',
  [Language.Danish]: 'Danish',
  [Language.Dari]: 'Dari',
  [Language.Dutch]: 'Dutch',
  [Language.English]: 'English',
  [Language.Estonian]: 'Estonian',
  [Language.Farsi_Persian]: 'Farsi Persian',
  [Language.Filipino_Tagalog]: 'Filipino Tagalog',
  [Language.Finnish]: 'Finnish',
  [Language.French]: 'French',
  [Language.French_Canada]: 'French (Canada)',
  [Language.Georgian]: 'Georgian',
  [Language.German]: 'German',
  [Language.Greek]: 'Greek',
  [Language.Gujarati]: 'Gujarati',
  [Language.Haitian_Creole]: 'Haitian Creole',
  [Language.Hausa]: 'Hausa',
  [Language.Hebrew]: 'Hebrew',
  [Language.Hindi]: 'Hindi',
  [Language.Hungarian]: 'Hungarian',
  [Language.Icelandic]: 'Icelandic',
  [Language.Indonesian]: 'Indonesian',
  [Language.Irish]: 'Irish',
  [Language.Italian]: 'Italian',
  [Language.Japanese]: 'Japanese',
  [Language.Kannada]: 'Kannada',
  [Language.Kazakh]: 'Kazakh',
  [Language.Korean]: 'Korean',
  [Language.Latvian]: 'Latvian',
  [Language.Lithuanian]: 'Lithuanian',
  [Language.Macedonian]: 'Macedonian',
  [Language.Malay]: 'Malay',
  [Language.Malayalam]: 'Malayalam',
  [Language.Maltese]: 'Maltese',
  [Language.Marathi]: 'Marathi',
  [Language.Mongolian]: 'Mongolian',
  [Language.Norwegian_Bokmal]: 'Norwegian Bokmal',
  [Language.Pashto]: 'Pashto',
  [Language.Polish]: 'Polish',
  [Language.Portuguese_Brazil]: 'Portuguese (Brazil)',
  [Language.Portuguese_Portugal]: 'Portuguese (Portugal)',
  [Language.Punjabi]: 'Punjabi',
  [Language.Romanian]: 'Romanian',
  [Language.Russian]: 'Russian',
  [Language.Serbian]: 'Serbian',
  [Language.Sinhala]: 'Sinhala',
  [Language.Slovak]: 'Slovak',
  [Language.Slovenian]: 'Slovenian',
  [Language.Somali]: 'Somali',
  [Language.Spanish]: 'Spanish',
  [Language.Spanish_Mexico]: 'Spanish (Mexico)',
  [Language.Swahili]: 'Swahili',
  [Language.Swedish]: 'Swedish',
  [Language.Tamil]: 'Tamil',
  [Language.Telugu]: 'Telugu',
  [Language.Thai]: 'Thai',
  [Language.Turkish]: 'Turkish',
  [Language.Ukrainian]: 'Ukrainian',
  [Language.Urdu]: 'Urdu',
  [Language.Uzbek]: 'Uzbek',
  [Language.Vietnamese]: 'Vietnamese',
  [Language.Welsh]: 'Welsh'
}
